/**
 * 전역 스크롤 관리 유틸리티
 * 모달, 팝업 등에서 일관된 스크롤 제어를 위해 사용
 */
class ScrollManager {
  constructor() {
    this.scrollPosition = 0;
    this.isLocked = false;
    this.scrollbarWidth = this.getScrollbarWidth();
    this.activeModals = new Set();
  }

  /**
   * 스크롤바 너비 계산
   */
  getScrollbarWidth() {
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar";
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  /**
   * 스크롤 잠금 (모달 열 때)
   */
  lock(modalId = "default") {
    this.activeModals.add(modalId);

    if (this.isLocked) {
      return; // 이미 잠겨있으면 중복 처리 방지
    }

    // 현재 스크롤 위치 저장
    this.scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    // iOS 감지
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    // 스크롤바 너비만큼 패딩 추가 (레이아웃 시프트 방지)
    document.documentElement.style.setProperty(
      "--scrollbar-width",
      `${this.scrollbarWidth}px`
    );

    // body 스타일 설정
    const body = document.body;
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${this.scrollPosition}px`;
    body.style.width = "100%";
    body.style.paddingRight = `${this.scrollbarWidth}px`;

    if (isIOS) {
      body.style.webkitOverflowScrolling = "touch";
      body.style.overscrollBehavior = "none";
      body.style.touchAction = "none";
      body.classList.add("no-scroll-ios");
    } else {
      body.classList.add("no-scroll");
    }

    body.classList.add("modal-open");
    this.isLocked = true;
  }

  /**
   * 스크롤 잠금 해제 (모달 닫을 때)
   */
  unlock(modalId = "default") {
    this.activeModals.delete(modalId);

    // 다른 모달이 여전히 활성화되어 있으면 스크롤 유지
    if (this.activeModals.size > 0) {
      return;
    }

    if (!this.isLocked) {
      return;
    }

    const body = document.body;

    // 스타일 초기화
    body.style.overflow = "";
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";
    body.style.paddingRight = "";
    body.style.webkitOverflowScrolling = "";
    body.style.overscrollBehavior = "";
    body.style.touchAction = "";

    // 클래스 제거
    body.classList.remove("no-scroll", "no-scroll-ios", "modal-open");

    // 원래 스크롤 위치로 복원
    window.scrollTo(0, this.scrollPosition);

    this.isLocked = false;
    this.scrollPosition = 0;
  }

  /**
   * 강제 정리 (에러 상황에서 사용)
   */
  forceUnlock() {
    this.activeModals.clear();
    this.unlock();
  }

  /**
   * 현재 잠금 상태 확인
   */
  isScrollLocked() {
    return this.isLocked;
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const scrollManager = new ScrollManager();

// 페이지 새로고침 시 정리
window.addEventListener("beforeunload", () => {
  scrollManager.forceUnlock();
});

// 에러 발생 시 정리
window.addEventListener("error", () => {
  scrollManager.forceUnlock();
});

export default scrollManager;
